import './BrowserPreviewer.less';
import React from 'react';
import * as ReactDOM from 'react-dom';
import FileUtil from '../../../common/util/FileUtil';
import { 
  CloseOutlined, 
  DownloadOutlined,
  PrinterOutlined 
} from '@ant-design/icons';
import MessageBoxUtil from '../../../common/util/MessageBoxUtil';
import Lang from '../../../common/model/global/Lang';
import Sun from '../../../common/model/global/Sun';

interface IProps {
  name: string;
  url: string;
  size: number;
  onClose: () => void;
  watermark?: {
    text?: string;
    fontSize?: number;
    color?: string;
    opacity?: number;
    rotate?: number;
  };
  hasDownloadPermission?: boolean;
  hasPrintPermission?: boolean;
  matter?: any;
  showToolbar?: boolean;
}

interface IState {
  showContextMenuWarning: boolean;
  printing: boolean;
  downloading: boolean;
}

interface ToolbarConfig {
  external: {
    show: boolean;
    canDownload: boolean;
    canPrint: boolean;
  };
  internal: {
    show: boolean;
    canDownload: boolean;
    canPrint: boolean;
  };
}

export default class BrowserPreviewer extends React.Component<IProps, IState> {
  private embedRef = React.createRef<HTMLEmbedElement>();
  private iframeRef = React.createRef<HTMLIFrameElement>();
  private watermarkRef = React.createRef<HTMLDivElement>();
  private imgRef = React.createRef<HTMLImageElement>();
  private videoRef = React.createRef<HTMLVideoElement>();
  private warningTimer: NodeJS.Timeout | null = null;
  // 简单的蒙版div引用
  private maskRef = React.createRef<HTMLDivElement>();
  
  state: IState = {
    showContextMenuWarning: false,
    printing: false,
    downloading: false
  };

  componentDidMount() {
    console.log('BrowserPreviewer 接收到参数:', {
      name: this.props.name,
      url: this.props.url,
      watermark: this.props.watermark,
      hasWatermark: !!this.props.watermark,
      hasDownloadPermission: this.props.hasDownloadPermission,
      hasPrintPermission: this.props.hasPrintPermission,
      showToolbar: this.props.showToolbar,
      isOfficePreview: this.isOfficePreviewUrl(this.props.url),
      isPDF: this.isPDF(),
      shouldDisableRightClick: this.shouldDisableRightClick()
    });

    // 初始化水印
    if (this.props.watermark && 
        typeof this.props.watermark === 'object' && 
        Object.keys(this.props.watermark).length > 0) {
      console.log('✅ 开始渲染水印...');
      this.renderWatermark();
    } else {
      console.log('❌ 未接收到有效水印参数，跳过水印渲染');
    }
    
    // 添加全局右键监听
    document.addEventListener('contextmenu', this.handleGlobalContextMenu, true);
  }

  componentWillUnmount() {
    // 移除事件监听
    document.removeEventListener('contextmenu', this.handleGlobalContextMenu, true);
    
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
    }
  }

  componentDidUpdate(prevProps: IProps) {
    if (this.props.watermark !== prevProps.watermark) {
      this.renderWatermark();
    }
  }

  // ==================== 权限判断 ====================
  private shouldDisableRightClick = (): boolean => {
    // 如果没有下载权限且没有打印权限，需要禁用右键
    return !this.props.hasDownloadPermission && !this.props.hasPrintPermission;
  };

  // ==================== 核心右键控制逻辑 ====================
  
  // 全局右键菜单处理
  private handleGlobalContextMenu = (e: MouseEvent) => {
    if (!this.shouldDisableRightClick()) {
      return;
    }
    
    // 检查事件是否发生在预览区域内
    const previewContainer = document.querySelector('.browser-previewer-wrapper');
    if (previewContainer && previewContainer.contains(e.target as Node)) {
      console.log('🚫 全局右键监听器捕获到预览区域右键');
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation?.();
      this.showContextMenuWarning();
      return false;
    }
  };

  // ✅ React JSX 中的 onContextMenu
  private handleContentContextMenu = (e: React.MouseEvent<any>) => {
    if (!this.shouldDisableRightClick()) {
      return;
    }
    
    console.log('🚫 组件右键监听器捕获到右键');
    e.preventDefault();
    e.stopPropagation();
    this.showContextMenuWarning();
  };

  // 显示右键警告
  private showContextMenuWarning = () => {
    console.log('⚠️ 显示右键警告');
    this.setState({ showContextMenuWarning: true });
    
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
    }
    
    this.warningTimer = setTimeout(() => {
      this.setState({ showContextMenuWarning: false });
    }, 2000);
  };

  // ==================== 工具栏配置 ====================
  
  private getToolbarConfig(): ToolbarConfig {
    const isOfficePreview = this.isOfficePreviewUrl(this.props.url);
    const isPdfFile = this.isPDF();
    const isVideoFile = this.isVideo();
    
    const hasDownloadPermission = this.props.hasDownloadPermission === true;
    const hasPrintPermission = this.props.hasPrintPermission === true;
    
    const useInternalToolbar = isOfficePreview || isPdfFile;
    
    return {
      external: {
        show: !useInternalToolbar && 
              (this.props.showToolbar !== undefined 
                ? this.props.showToolbar 
                : (hasDownloadPermission || hasPrintPermission)),
        canDownload: hasDownloadPermission,
        canPrint: hasPrintPermission && !isVideoFile
      },
      internal: {
        show: useInternalToolbar,
        canDownload: hasDownloadPermission,
        canPrint: hasPrintPermission
      }
    };
  }

  // ==================== 静态方法 ====================
  
  static showCustom(title: string, htmlContent: string, fileSize?: number): void {
    console.log('显示CAD预览:', title);
    
    const previewWindow = window.open('', '_blank');
    if (previewWindow) {
      previewWindow.document.write(htmlContent);
      previewWindow.document.close();
    }
  }

  static show(
    name: string, 
    url: string, 
    size: number, 
    watermark?: IProps['watermark'],
    hasDownloadPermission?: boolean,
    hasPrintPermission?: boolean,
    matter?: any,
    showToolbar?: boolean
  ) {
    console.log('🎬 BrowserPreviewer.show 被调用');
    console.log('  - 标题:', name);
    console.log('  - URL:', url);
    console.log('  - 大小:', size);
    console.log('  - 水印:', watermark);
    console.log('  - 下载权限:', hasDownloadPermission);
    console.log('  - 打印权限:', hasPrintPermission);
    console.log('  - 显示工具栏:', showToolbar);
    console.log('  - 是Office预览:', url.includes('/onlinePreview') || url.includes('kkFileView'));
    console.log('  - 是PDF文件:', name.toLowerCase().endsWith('.pdf'));
    
    const div = document.createElement('div');
    document.body.appendChild(div);

    const cleanup = () => {
      ReactDOM.unmountComponentAtNode(div);
      div.remove();
    };

    ReactDOM.render(
      <BrowserPreviewer
        name={name}
        url={url}
        size={size}
        watermark={watermark}
        hasDownloadPermission={hasDownloadPermission}
        hasPrintPermission={hasPrintPermission}
        matter={matter}
        onClose={cleanup}
        showToolbar={showToolbar}
      />,
      div
    );

    return cleanup;
  }

  // ==================== 文件类型判断 ====================
  
  private isOfficePreviewUrl = (url: string): boolean => {
    return url.includes('/onlinePreview') || url.includes('kkFileView');
  };

  private isPDF(): boolean {
    return this.props.name.toLowerCase().endsWith('.pdf');
  }

  private isImage(): boolean {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
    const fileName = this.props.name.toLowerCase();
    return imageExtensions.some(ext => fileName.endsWith(ext));
  }

  private isVideo(): boolean {
    const videoExtensions = ['.mp4', '.avi', '.mov', '.mkv', '.webm', '.flv', '.wmv', '.m4v'];
    const fileName = this.props.name.toLowerCase();
    return videoExtensions.some(ext => fileName.endsWith(ext));
  }

  // ==================== 下载功能 ====================
  
  private handleDownload = () => {
    const { internal } = this.getToolbarConfig();
    
    if (!internal.canDownload && !this.props.hasDownloadPermission) {
      MessageBoxUtil.warning(Lang.t('matter.noDownloadPermission'));
      return;
    }

    this.setState({ downloading: true });

    try {
      let downloadUrl = this.props.url;
      
      const isOfficePreview = this.isOfficePreviewUrl(this.props.url);
      
      if (isOfficePreview && this.props.matter) {
        if (this.props.matter.getDownloadUrl) {
          downloadUrl = this.props.matter.getDownloadUrl();
        } else if (this.props.matter.getPreviewUrl) {
          downloadUrl = this.props.matter.getPreviewUrl();
        }
      }

      console.log('下载文件，使用URL:', downloadUrl);
      
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = this.props.name;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      MessageBoxUtil.success(Lang.t('matter.downloadStarted'));
    } catch (error) {
      console.error('下载失败:', error);
      MessageBoxUtil.error(Lang.t('matter.downloadFailed'));
    } finally {
      setTimeout(() => {
        this.setState({ downloading: false });
      }, 1000);
    }
  };

  // ==================== 打印功能 ====================
  
  private handlePrint = () => {
    const { internal } = this.getToolbarConfig();
    
    if (!internal.canPrint && !this.props.hasPrintPermission) {
      MessageBoxUtil.warning(Lang.t('matter.noDownloadPermission'));
      return;
    }

    this.setState({ printing: true });

    const isPDF = this.isPDF();
    const isImage = this.isImage();

    try {
      if (isImage && this.imgRef.current) {
        this.printImage();
      } else if (isPDF) {
        this.printPDF();
      } else {
        this.printIframeContent();
      }
    } catch (error) {
      console.error('打印失败:', error);
      MessageBoxUtil.error(Lang.t('matter.printFailed'));
      this.setState({ printing: false });
    }
  };

  private printIframeContent = () => {
    const iframe = this.iframeRef.current;
    
    if (!iframe || !iframe.contentWindow) {
      console.log('打印失败: iframe 不可用，尝试降级方案');
      this.fallbackPrintSolution();
      return;
    }

    try {
      console.log('尝试调用iframe内部打印功能...');
      
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      
      setTimeout(() => {
        this.setState({ printing: false });
        MessageBoxUtil.success('打印指令已发送到浏览器，请在打印对话框中进行设置。');
      }, 1000);
      
    } catch (error) {
      console.warn('直接调用iframe打印失败，错误详情:', error);
      console.log('iframe内容可能跨域，尝试降级方案...');
      this.fallbackPrintSolution();
    }
  };

  private fallbackPrintSolution = () => {
    this.setState({ printing: false });
  
    let printUrl = this.props.url;
    console.log('原始打印URL:', printUrl);
  
    const targetParam = 'officePreviewType=pdf';
    
    if (printUrl.includes('officePreviewType=')) {
      printUrl = printUrl.replace(/officePreviewType=[^&]*/, targetParam);
    } else {
      const separator = printUrl.includes('?') ? '&' : '?';
      printUrl = `${printUrl}${separator}${targetParam}`;
    }
    
    if (printUrl.includes('#')) {
      printUrl = printUrl.split('#')[0];
    }

    console.log('转换后的PDF预览URL:', printUrl);

    const windowFeatures = 'width=1200,height=800,scrollbars=yes,location=yes';
    const printWindow = window.open(printUrl, '_blank', windowFeatures);

    if (!printWindow) {
      MessageBoxUtil.error('弹出窗口被浏览器阻止。请检查浏览器设置，或按住 Ctrl 键再点击打印按钮。');
      return;
    }

    MessageBoxUtil.info('正在新窗口打开PDF预览模式，加载完成后即可使用打印功能。');

    printWindow.onload = () => {
      setTimeout(() => {
        try {
          const guideHtml = `
            <div id="kk-custom-guide" style="
              position: fixed;
              top: 10px;
              left: 50%;
              transform: translateX(-50%);
              background: #1890ff;
              color: white;
              padding: 10px 20px;
              border-radius: 6px;
              box-shadow: 0 4px 12px rgba(0,0,0,0.3);
              z-index: 100000;
              font-family: Arial, sans-serif;
              font-size: 14px;
              text-align: center;
              min-width: 300px;
            ">
              <strong>📄 文档打印指引</strong><br>
              <span>现在处于<b>PDF预览模式</b>，您可以使用浏览器自带的打印功能。</span>
              <button onclick="document.getElementById('kk-custom-guide').style.display='none';" style="
                background: transparent;
                color: white;
                border: 1px solid rgba(255,255,255,0.5);
                margin-left: 10px;
                padding: 2px 8px;
                border-radius: 4px;
                cursor: pointer;
                font-size: 12px;
              " title="关闭提示">
                ×
              </button>
            </div>
          `;
          
          printWindow.document.body.insertAdjacentHTML('afterbegin', guideHtml);
          console.log('已注入PDF预览模式打印引导');
          
        } catch (e) {
          console.log('无法注入引导（跨域），但PDF预览模式已启用，用户可自行按 Ctrl+P');
        }
      }, 1500);
    };

    setTimeout(() => {
      if (printWindow && !printWindow.closed) {
        console.log('新窗口仍在打开状态，PDF预览模式应已加载');
      }
    }, 3000);
  };

  private printImage = () => {
    if (!this.imgRef.current) return;

    const img = this.imgRef.current;
    const printWindow = window.open('', '_blank');
    
    if (!printWindow) {
      MessageBoxUtil.error(Lang.t('matter.printPopupBlocked'));
      this.setState({ printing: false });
      return;
    }

    const html = `
      <html>
        <head>
          <title>${this.props.name}</title>
          <style>
            body { 
              margin: 0; 
              padding: 20px; 
              display: flex; 
              justify-content: center; 
              align-items: center;
              min-height: 100vh;
            }
            img { 
              max-width: 100%; 
              max-height: 100vh;
              object-fit: contain;
            }
            @media print {
              body { padding: 0; }
            }
          </style>
          <script>
            window.onload = function() {
              window.print();
              setTimeout(() => {
                window.close();
              }, 100);
            };
          </script>
        </head>
        <body>
          <img src="${img.src}" alt="${this.props.name}" />
        </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();

    setTimeout(() => {
      if (printWindow && !printWindow.closed) {
        printWindow.close();
      }
      this.setState({ printing: false });
    }, 5000);
  };

  private printPDF = () => {
    const printWindow = window.open(this.props.url, '_blank');
    
    if (!printWindow) {
      MessageBoxUtil.error(Lang.t('matter.printPopupBlocked'));
      this.setState({ printing: false });
      return;
    }

    MessageBoxUtil.info(Lang.t('matter.pdfPrintHint'));
    
    setTimeout(() => {
      this.setState({ printing: false });
    }, 1000);
  };

  // ==================== PDF URL处理 ====================
  
  private getPdfUrl(): string {
    const { internal } = this.getToolbarConfig();
    const url = this.props.url;
    
    if (url.startsWith('blob:')) {
      return url;
    }
    
    let cleanUrl = url;
    if (cleanUrl.includes('#toolbar=')) {
      cleanUrl = cleanUrl.split('#')[0];
    }
    
    const toolbarValue = (internal.canDownload || internal.canPrint) ? 1 : 0;
    const separator = cleanUrl.includes('?') ? '&' : '?';
    return `${cleanUrl}${separator}#toolbar=${toolbarValue}`;
  }

  // ==================== 水印渲染 ====================
  
  private renderWatermark() {
    const watermarkEl = this.watermarkRef.current;
    if (!watermarkEl || !this.props.watermark) {
      console.log('水印渲染条件不满足');
      return;
    }

    const {
      text = '预览文件 - 请勿外传',
      fontSize = 36,
      color = 'rgba(128, 128, 128, 0.3)',
      opacity = 0.3,
      rotate = -30
    } = this.props.watermark;

    console.log('渲染水印参数:', { text, fontSize, color, opacity, rotate });

    watermarkEl.innerHTML = '';

    const watermarkContainer = document.createElement('div');
    watermarkContainer.className = 'watermark-container';
    watermarkContainer.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 1000;
      overflow: hidden;
      user-select: none;
    `;

    const containerWidth = watermarkEl.clientWidth || 800;
    const containerHeight = watermarkEl.clientHeight || 600;
    
    const watermarkWidth = 400;
    const watermarkHeight = 200;
    
    const cols = Math.ceil(containerWidth / watermarkWidth) + 1;
    const rows = Math.ceil(containerHeight / watermarkHeight) + 1;

    for (let i = 0; i < rows; i++) {
      for (let j = 0; j < cols; j++) {
        const watermarkItem = document.createElement('div');
        watermarkItem.style.cssText = `
          position: absolute;
          top: ${i * watermarkHeight}px;
          left: ${j * watermarkWidth}px;
          font-size: ${fontSize}px;
          color: ${color};
          opacity: ${opacity};
          transform: rotate(${rotate}deg);
          transform-origin: center center;
          white-space: nowrap;
          pointer-events: none;
          user-select: none;
          z-index: 1001;
          font-weight: bold;
          font-family: Arial, sans-serif;
        `;
        watermarkItem.textContent = text;
        watermarkContainer.appendChild(watermarkItem);
      }
    }

    watermarkEl.appendChild(watermarkContainer);
    console.log('水印渲染完成，创建了', rows * cols, '个水印元素');
  }

  // ==================== 渲染方法 ====================
  
  render() {
    const { watermark } = this.props;
    const { showContextMenuWarning, printing, downloading } = this.state;
    const isPDF = this.isPDF();
    const isImage = this.isImage();
    const isVideo = this.isVideo();
    const isOfficePreview = this.isOfficePreviewUrl(this.props.url);
    const pdfUrl = isPDF ? this.getPdfUrl() : this.props.url;
    const isMobilePDF = isPDF && Sun.getSingleton().isMobile;
    const pdfEmbedUrl = isMobilePDF ? this.props.url.replace('/api/alien/preview/', '/api/alien/preview/html/') : pdfUrl;
    
    const toolbarConfig = this.getToolbarConfig();
    const shouldDisableRightClick = this.shouldDisableRightClick();
    const isOfficeWithDisabledRightClick = isOfficePreview && shouldDisableRightClick;
    
    console.log('工具栏配置:', {
      fileType: isPDF ? 'PDF' : (isOfficePreview ? 'Office' : (isImage ? '图片' : (isVideo ? '视频' : '其他'))),
      config: toolbarConfig,
      shouldDisableRightClick,
      isOfficeWithDisabledRightClick
    });

    return (
      <div
        className="browser-previewer-wrapper"
        onClick={() => this.props.onClose()}
      >
        <div className="browser-previewer" onClick={(e) => e.stopPropagation()}>
          {/* 标题栏 */}
          <div className="title-bar">
            <div className="left-part"></div>
            
            <div className="middle-part">
              {this.props.name} ({FileUtil.humanFileSize(this.props.size)})
            </div>
            
            <div className="right-part">
              {/* 外部自定义工具栏区域 */}
              {toolbarConfig.external.show ? (
                <div className="action-buttons">
                  {/* 下载按钮 */}
                  {toolbarConfig.external.canDownload && (
                    <button
                      className="action-btn download-btn"
                      onClick={this.handleDownload}
                      disabled={downloading}
                      title={Lang.t('matter.download')}
                    >
                      <DownloadOutlined />
                      <span>{downloading ? Lang.t('matter.downloading') : Lang.t('matter.download')}</span>
                    </button>
                  )}
                  
                  {/* 打印按钮 */}
                  {toolbarConfig.external.canPrint && (
                    <button
                      className="action-btn print-btn"
                      onClick={this.handlePrint}
                      disabled={printing}
                      title={Lang.t('matter.print')}
                    >
                      <PrinterOutlined />
                      <span>{printing ? Lang.t('matter.printing') : Lang.t('matter.print')}</span>
                    </button>
                  )}
                  
                  {/* 关闭按钮 */}
                  <div 
                    className="close btn-action"
                    onClick={() => this.props.onClose()}
                    title={Lang.t('close')}
                  >
                    <CloseOutlined />
                  </div>
                </div>
              ) : (
                // 不显示外部工具栏时，只显示关闭按钮
                <div 
                  className="close btn-action"
                  onClick={() => this.props.onClose()}
                  title={Lang.t('close')}
                >
                  <CloseOutlined />
                </div>
              )}
            </div>
          </div>
          
          {/* 右键警告提示 */}
          {showContextMenuWarning && (
            <div className="context-menu-warning">
              右键菜单已禁用
            </div>
          )}
          
          <div className="frame-area">
            {/* 水印层 */}
            {watermark && (
              <div 
                ref={this.watermarkRef}
                className="watermark-layer"
              />
            )}
            
            {/* 内容层 */}
            <div className="content-area">
              {isPDF ? (
                <div 
                  className="pdf-container" 
                  style={{ 
                    position: 'relative', 
                    width: '100%', 
                    height: '100%',
                    overflow: 'hidden'
                  }}
                >
                  {/* PDF预览 - 参考文章的简单蒙版方案 */}
                  {shouldDisableRightClick && (
                    <div
                      ref={this.maskRef}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: '10px', // 留出滚动条位置
                        bottom: 0,
                        zIndex: 9999,
                        backgroundColor: 'transparent',
                        pointerEvents: 'auto',
                        cursor: 'default'
                      }}
                      onContextMenu={this.handleContentContextMenu}
                    />
                  )}
                  
                  {isMobilePDF ? (
                    <iframe
                      src={pdfEmbedUrl}
                      width="100%"
                      height="100%"
                      title={this.props.name}
                      style={{ 
                        width: '100%',
                        height: '100%',
                        backgroundColor: '#fff',
                        border: 'none',
                        margin: 0,
                        padding: 0
                      }}
                    />
                  ) : (
                    <embed
                      ref={this.embedRef}
                      src={pdfUrl}
                      type="application/pdf"
                      width="100%"
                      height="100%"
                      style={{ 
                        display: 'block',
                        // 让PDF正常显示
                        width: '100%',
                        height: '100%',
                        backgroundColor: '#fff',
                        border: 'none',
                        margin: 0,
                        padding: 0
                      }}
                    />
                  )}
                </div>
              ) : isImage ? (
                <div className="image-preview-container">
                  <img
                    ref={this.imgRef}
                    src={this.props.url}
                    alt={this.props.name}
                    draggable="false"
                    style={{ 
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      margin: 'auto',
                      display: 'block'
                    }}
                    onContextMenu={shouldDisableRightClick ? this.handleContentContextMenu : undefined}
                  />
                </div>
              ) : isVideo ? (
                <div className="video-preview-container">
                  <video
                    ref={this.videoRef}
                    controls
                    autoPlay
                    playsInline
                    style={{ 
                      width: '100%', 
                      height: '100%',
                      objectFit: 'contain',
                      backgroundColor: '#000'
                    }}
                    onContextMenu={shouldDisableRightClick ? this.handleContentContextMenu : undefined}
                  >
                    <source src={this.props.url} type="video/mp4" />
                    您的浏览器不支持视频播放
                  </video>
                </div>
              ) : (
                <div 
                  className="iframe-container" 
                  style={{ 
                    position: 'relative', 
                    width: '100%', 
                    height: '100%',
                    overflow: 'hidden'
                  }}
                >
                  {/* Office预览 - 同样使用简单蒙版方案 */}
                  {isOfficeWithDisabledRightClick && (
                    <div
                      ref={this.maskRef}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: '10px', // 留出滚动条位置
                        bottom: 0,
                        zIndex: 9999,
                        backgroundColor: 'transparent',
                        pointerEvents: 'auto',
                        cursor: 'default'
                      }}
                      onContextMenu={this.handleContentContextMenu}
                    />
                  )}
                  
                  {/* Office预览 iframe */}
                  <iframe
                    ref={this.iframeRef}
                    src={this.props.url}
                    width="100%"
                    height="100%"
                    title="文件预览"
                    style={{ 
                      border: 'none',
                      // Office预览样式
                      width: '100%',
                      height: '100%',
                      backgroundColor: '#fff',
                      // 确保可以接收键盘焦点
                      zIndex: 1
                    }}
                    sandbox={isOfficePreview ? "allow-same-origin allow-scripts allow-popups" : undefined}
                    // 确保可以接收键盘焦点
                    tabIndex={0}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}